/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useState, useEffect } from "react";
import { Typography, FormControlLabel, FormGroup, Switch, TextField } from "@mui/material";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-html";
import "ace-builds/src-noconflict/theme-chrome";
import "ace-builds/src-noconflict/ext-language_tools";
import "ace-builds/src-noconflict/ext-spellcheck";
import { isStringNotEmpty } from "utils/empty-exists";
import { Logger } from "cms/utils/log/logger";
import { useUpdateComponent } from "cms/components/hooks/use-update-component";

const editorTheme = "chrome";

const YoutubeComponentEditor = () => {
    
    const { component, update } = useUpdateComponent();
    const {
        guid,
        useEmbed: _useEmbed,
        forceResponsive: _forceResponsive,
        embed: _embed,
        // title: _title,
        // srcId: _srcId,
        src: _src,
        // width: _width,
        height: _height,
    } = component || {};
    
    const [useEmbed, setUseEmbed] = useState(_useEmbed);
    const [forceResponsive, setForceResponsive] = useState(_forceResponsive);
    const [embed, setEmbed] = useState(_embed);
    const [editorState, setEditorState] = useState(_embed);
    const [src, setSrc] = useState(_src);
    const [height, setHeight] = useState(_height);
    const hasContent = isStringNotEmpty(embed);
    const forcedWidth = "100%";
    
    // const [title, setTitle] = useState(_title)
    // const [srcId, setSrcId] = useState(_srcId)
    // const [width, setWidth] = useState(_width)
    
    const onChangeUseEmbedToggle = (event) => {
        const checked = event.target.checked;
        const nextChecked = Boolean(checked).valueOf();
        setUseEmbed(nextChecked);
        update("useEmbed", nextChecked);
    };
    const onChangeForceResponsive = (event) => {
        const checked = event.target.checked;
        const nextChecked = Boolean(checked).valueOf();
        setForceResponsive(nextChecked);
        update("forceResponsive", nextChecked);
    };
    const handleEditorChange = (html) => {
        setEditorState(html);
        setEmbed(html);
        update("embed", html);
    };
    const onChangeSrc = (event) => {
        const _src = event.target.value;
        const { src, srcid } = handleSrcChange(_src);
        setSrc(src);
        // setSrcId(srcid)
        update("src", src);
        update("srcid", srcid);
    };
    const onChangeWidth = (event) => {
        const width = event.target.value;
        // setWidth(width)
        update("width", width);
    };
    const onChangeHeight = (event) => {
        const height = event.target.value;
        setHeight(height);
        update("height", height);
    };
    
    const handleSrcChange = (new_value) => {
        if (!new_value) {
            return { src: "" };
        }
        let code = "";
        let built_url = "";
        let found = false;

        const url = new URL(new_value);
        const isYT = url.hostname === "youtu.be" || url.hostname === "www.youtube.com";
        // https://www.youtube.com/embed/L1O8hItg6LI
        if (isYT) {
            const path = url.pathname;
            const search = url.search;
            if (path.startsWith("/embed/")) {
                const key = "/embed/";
                code = path.slice(path.indexOf(key) + key.length);
                built_url = `https://www.youtube.com/embed/${code}`;
                Logger.debug("1 youtube: ", code);
                // found = true
                return { src: built_url, srcid: code };
            }
            if (url.search.includes("?v=")) {
                const key = "?v=";
                code = search.slice(search.indexOf(key) + key.length);
                built_url = `https://www.youtube.com/embed/${code}`;
                Logger.debug("2 youtube: ", code);
                // found = true
                return { src: built_url, srcid: code };
            }
            if (url.hostname === "youtu.be") {
                code = path.slice(1);
                Logger.debug("3 youtube: ", code);
                // found = true
                return { src: new_value, srcid: code };
            }
            if (!found) {
                return { src: new_value };
            }
        } else {
            built_url = new_value;
            Logger.debug("4 youtube: ", built_url);
            return { src: built_url };
        }
    };
    
    useEffect(() => {
        if (!editorState) {
            // only on load
            const initialHTML = hasContent ? embed : "<!-- SimpliCity CMS Youtube placeholder -->\n";
            setEditorState(initialHTML);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editorState]);
    
    return (
        <div className='simplicity-editor-frame' style={{ minHeight: "300px" }} >
            <Typography variant='overline' color={"text.secondary"}>
                Embed Code?
            </Typography>
            <FormGroup>
                <FormControlLabel
                    control={
                        <Switch
                            checked={useEmbed}
                            onChange={onChangeUseEmbedToggle}
                        />
                    }
                    label='Use HTML Embed'
                />
            </FormGroup>
            
            {useEmbed && (
                <>
                    <Typography variant='overline' color={"text.secondary"}>
                        Embed HTML code:
                    </Typography>
                    {editorState &&
                        <AceEditor
                            value={editorState}
                            mode='html'
                            theme={editorTheme}
                            onChange={handleEditorChange}
                            name={`simplicity-json-editor-${guid}`}
                            editorProps={{ $blockScrolling: true }}
                            setOptions={{
                                useWorker: false,
                                enableBasicAutocompletion: false,
                                enableLiveAutocompletion: false,
                                enableSnippets: false,
                                showPrintMargin: false,
                            }}
                            width='100%'
                            tabsize='2'
                        />
                    }
                    <p style={{
                        padding: "8px",
                        fontSize: "12px", fontStyle: "italic",
                        color: "blue",
                    }}
                    >
                        Note: Website CSS forces the width of a youtube video to be 100% of the parent container that this
                        video is placed into. Height is used, but width added here is likely to be ignored.
                    </p>
                </>
            )}
            {!useEmbed && (
                <>
                    <Typography variant='overline' color={"text.secondary"}>
                        Video Url:
                    </Typography>
                    <TextField
                        id='youtube_src'
                        fullWidth={true}
                        value={src}
                        onChange={onChangeSrc}
                    />
                    
                    <Typography variant='overline' color={"text.secondary"}>
                        Make Responsive?
                    </Typography>
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={forceResponsive}
                                    onChange={onChangeForceResponsive}
                                />
                            }
                            label='Use 16/9 Aspect Ratio'
                        />
                    </FormGroup>
                    <Typography variant='overline' color={"text.secondary"}>
                        Width:
                    </Typography>
                    <TextField
                        id='youtube_width'
                        fullWidth={true}
                        // value={width}
                        value={forcedWidth}
                        onChange={onChangeWidth}
                        disabled={true}
                    />
                    <Typography variant='overline' color={"text.secondary"}>
                        Height:
                    </Typography>
                    <TextField
                        id='youtube_height'
                        fullWidth={true}
                        value={height}
                        onChange={onChangeHeight}
                        disabled={forceResponsive}
                    />
                </>
            )}
        </div>
    );
};

YoutubeComponentEditor.displayName = "YoutubeComponentEditor";
export { YoutubeComponentEditor };
